
import styles from './Dashboard.module.scss';
import { Title } from '@shared/components/typography';
import { TitleTag, TitleVariant } from '@shared/components/typography/Title';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    XAxis,
    CartesianGrid,
    LineChart,
    YAxis,
    Line,
} from 'recharts';
import { useCallback, useMemo, useState } from "react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { randomHexNotTooLight } from "@shared/helpers/colorHelper";
import { getBahaviouralLabelName, normalizeRange } from "@shared/helpers/dateHelper";
import Button from "@shared/components/Button/Button";
import { primaryDefaultColour, secondaryDefaultColour } from "@shared/common/constants/ThemeDefaultColour";
import { FrownOutlined, MehOutlined, SmileOutlined } from '@ant-design/icons';
import { ChartLineItem, ChartRatingItem, PickerMode } from "@shared/common/types/main";
import { PICKER_MODE } from '@shared/common/constants/PickerMode';
import { DashboardProps } from "./DashboardProps";
import ComponentLoader from '@shared/components/ComponentLoader/ComponentLoader';
import ComponentValueChecker from '@shared/components/ComponentValueChecker/ComponentValueChecker';
import updateLocale from "dayjs/plugin/updateLocale";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import classNames from 'classnames';
import DateRangeFilter from '@shared/components/DateRangeFilter';

dayjs.extend(isoWeek);
dayjs.extend(updateLocale);
dayjs.extend(utc);
const lang = navigator.language.toLowerCase();
dayjs.locale(lang.startsWith("pl") ? "pl" : "en");
dayjs.updateLocale(dayjs.locale(), { weekStart: 1 });

type PieLabelProps = {
    x?: number;
    y?: number;
    cx?: number;
    textAnchor?: string;
    name?: string;
    value?: number;
    fill?: string;
    payload?: {
        colour?: string;
        fill?: string;
    };
};

type PieLabelLineProps = {
    points?: Array<{
        x: number;
        y: number;
    }>;
    stroke?: string;
};

const PIE_LABEL_MAX_WORDS_PER_LINE = 5;
const PIE_LABEL_MAX_CHARS_PER_LINE = 20;
const PIE_LABEL_LINE_HEIGHT = 12;
const PIE_LABEL_LINE_GAP = 2;

const splitPieLabelName = (name: string): string[] => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];

    const lines: string[] = [];
    let currentLineWords = 0;
    let currentLine = "";

    words.forEach((word) => {
        const nextCandidate = currentLine ? `${currentLine} ${word}` : word;
        const exceedsWordsLimit = currentLineWords >= PIE_LABEL_MAX_WORDS_PER_LINE;
        const exceedsWidthFallback = nextCandidate.length > PIE_LABEL_MAX_CHARS_PER_LINE;

        if (currentLine && (exceedsWordsLimit || exceedsWidthFallback)) {
            lines.push(currentLine);
            currentLine = word;
            currentLineWords = 1;
            return;
        }

        currentLine = nextCandidate;
        currentLineWords += 1;
    });

    if (currentLine) lines.push(currentLine);
    return lines;
};

const renderPieLabelLine = ({ points, stroke }: PieLabelLineProps) => {
    if (!points || points.length < 2) {
        return <path d="" fill="none" stroke="none" className="recharts-pie-label-line" />;
    }

    const [startPoint, endPoint] = points;
    const direction = endPoint.x >= startPoint.x ? 1 : -1;
    const adjustedEndX = endPoint.x - (direction * PIE_LABEL_LINE_GAP);

    return (
        <path
            d={`M${startPoint.x},${startPoint.y}L${adjustedEndX},${endPoint.y}`}
            fill="none"
            stroke={stroke}
            className="recharts-pie-label-line"
        />
    );
};

const renderNameValuePieLabel = ({ x = 0, y = 0, cx = 0, textAnchor = "middle", name = "", value = 0, fill, payload }: PieLabelProps) => {
    const nameLines = splitPieLabelName(`${name}: ${value}`);
    const labelLines = nameLines.length > 0
        ? [...nameLines.slice(0, -1), `${nameLines[nameLines.length - 1]}`]
        : [`${value}`];
    const centeredY = y - ((labelLines.length - 1) * PIE_LABEL_LINE_HEIGHT) / 2;
    const resolvedTextAnchor = x > cx ? 'start' : x < cx ? 'end' : textAnchor;
    const labelX = resolvedTextAnchor === 'start'
        ? x + PIE_LABEL_LINE_GAP
        : resolvedTextAnchor === 'end'
            ? x - PIE_LABEL_LINE_GAP
            : x;

    return (
        <text
            x={labelX}
            y={centeredY}
            fill={fill ?? payload?.colour ?? payload?.fill ?? "#171542"}
            textAnchor={resolvedTextAnchor}
            dominantBaseline="central"
        >
            {labelLines.map((line, index) => (
                <tspan key={`${line}_${index}`} x={labelX} dy={index === 0 ? 0 : PIE_LABEL_LINE_HEIGHT}>
                    {line}
                </tspan>
            ))}
        </text>
    );
};

const Dashboard = (props: DashboardProps) => {
    const { theme, queryArgs, filterDate, setFilters, askedQuestionsRequest, averageDocumentsRequest, averageQuestionsRequest,
        behaviouralUsageRequest, exportQA, feedbackRatingsRequest, insightsUsageRequest,
        organisationUsersRequest, totalTransactionsRequest, userBehavioursRequest, callbackRequestsRequest,
        userRequest
    } = props;

    const { data: user } = userRequest;
    const { data: askedQuestions, isFetching: isFetchingAskedQuestions, isError: isErrorAskedQuestions } = askedQuestionsRequest;
    const { data: averageQuestions, isFetching: isFetchingAverageQuestions, isError: isErrorAverageQuestions } = averageQuestionsRequest;
    const { data: organisationUsers, isFetching: isFetchingOrganisationUsers, isError: isErrorOrganisationUsers } = organisationUsersRequest;
    const { data: totalTransactions, isFetching: isFetchingTotalTransactions, isError: isErrorTotalTransactions } = totalTransactionsRequest;
    const { data: insightUsage, isFetching: isFetchingInsightUsage, isError: isErrorInsightUsage } = insightsUsageRequest;
    const { data: behaviouralUsage, isFetching: isFetchingBehaviouralUsage, isError: isErrorBehaviouralUsage } = behaviouralUsageRequest;
    const { data: feedbackRatings, isFetching: isFetchingFeedbackRatings, isError: isErrorFeedbackRatings } = feedbackRatingsRequest;
    const { data: userBehaviours, isFetching: isFetchingUserBehaviours, isError: isErrorUserBehaviours } = userBehavioursRequest;
    const { data: averageDocs, isFetching: isFetchingAverageDocs, isError: isErrorAverageDocs } = averageDocumentsRequest;
    const callbackRequests = callbackRequestsRequest?.data;
    const isFetchingCallbackRequests = callbackRequestsRequest?.isFetching ?? false;
    const isErrorCallbackRequests = callbackRequestsRequest?.isError ?? false;
    const { download, data: exportQAData } = exportQA;
    const dashboardView = props.dashboardView ?? 'all';
    const renderContent = props.renderContent ?? ((content) => content);
    const showQa = dashboardView === 'all' || dashboardView === 'qa';
    const showUsers = dashboardView === 'all' || dashboardView === 'users';
    const [dateType, setDateType] = useState<PickerMode>(PICKER_MODE.Date);

    const onExportData = useCallback(async () => {
        try {
            await download({
                ...queryArgs,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }).unwrap();
        } catch {
            props.onExportFailure?.();
        }
    }, [queryArgs, download, props.onExportFailure]);

    const onDateFilterChange = useCallback((dates: [Dayjs | null, Dayjs | null] | null) => {
        const normalized = normalizeRange(dateType, dates);

        const from = normalized?.[0] ?? null;
        const to = normalized?.[1] ?? null;
        setFilters((p) => ({ ...p, start: from!, end: to! }));
    }, [dateType, setFilters]);

    const onDateTypeChange = useCallback((value: PickerMode) => {
        setDateType(value);
        props.onDateGroupingChange?.(value);
    }, [props.onDateGroupingChange]);

    const organisationUsersMapped = useMemo(() => {
        if (!organisationUsers) return [];

        return [
            {
                "name": "Active Users",
                "value": organisationUsers.activeCount,
                "colour": theme?.primary_color ?? primaryDefaultColour
            },
            {
                "name": "Inactive Users",
                "value": organisationUsers.usersCount - organisationUsers.activeCount,
                "colour": theme?.secondary_color ?? secondaryDefaultColour
            }
        ];
    }, [organisationUsers, theme?.primary_color, theme?.secondary_color]);

    const insightUsageMapped = useMemo(() => {
        if (!insightUsage || (insightUsage && insightUsage.insights.length === 0)) return [];

        return insightUsage.insights.map(x => ({
            "name": x.name,
            "value": x.count,
            "colour": randomHexNotTooLight()
        }));
    }, [insightUsage]);

    const userBehavioursMapped = useMemo(() => {
        if (!userBehaviours ||
            (userBehaviours.oneTimeUsers === 0 && userBehaviours.returnUsers === 0)) return [];
        return [
            {
                "name": "One Time Users",
                "value": userBehaviours.oneTimeUsers,
                "colour": theme?.primary_color ?? primaryDefaultColour
            },
            {
                "name": "Returned Users",
                "value": userBehaviours.returnUsers,
                "colour": theme?.secondary_color ?? secondaryDefaultColour
            },
        ];
    }, [userBehaviours, theme?.primary_color, theme?.secondary_color]);

    const feedbackRatingsMapped = useMemo(() => {
        if (!feedbackRatings) return [];
        return [
            {
                value: feedbackRatings.sadCount,
                icon: <FrownOutlined style={{ fontSize: 24, color: '#EF665A' }} />,
            },
            {
                value: feedbackRatings.neutralCount,
                icon: <MehOutlined style={{ fontSize: 24, color: '#FAE387' }} />,
            },
            {
                value: feedbackRatings.smileCount,
                icon: <SmileOutlined style={{ fontSize: 24, color: '#40B41C' }} />,
            },
        ] as ChartRatingItem[];
    }, [feedbackRatings]);

    const behaviouralUsageMapped = useMemo(() => {
        if (!behaviouralUsage || (behaviouralUsage && behaviouralUsage.items.length === 0)) return [];

        return behaviouralUsage.items.map(x => ({
            "name": getBahaviouralLabelName(x, behaviouralUsage.type),
            "value": x.count
        })) as ChartLineItem[];
    }, [behaviouralUsage]);

    return (
        <div>
            <Title tag={TitleTag.h1} variant={TitleVariant.main}>
                <span>Welcome {user?.first_name}</span>
            </Title>
            <div className={styles.wrapper} data-dashboard-view={dashboardView}>
                <div className={styles.headerMini}>
                    <Button type="button" onClick={onExportData} disabled={exportQAData.isLoading}>
                        {!exportQAData.isLoading ? 'Download Data' : 'Downloading...'}
                    </Button>
                    <DateRangeFilter
                        value={[filterDate.start, filterDate.end]}
                        onChange={onDateFilterChange}
                        dateType={dateType}
                        onDateTypeChange={(value) => onDateTypeChange(value as PickerMode)}
                        showTypeSelector
                        allowEmpty={[false, false]}
                        allowClear={false}
                        disabledDate={(current) => current && current.isAfter(dayjs().endOf("day"))}
                    />
                </div>
                {props.afterControls}
                {renderContent(<>
                {showQa && <div className={styles.kpiRow}>
                    <div className={classNames(styles.col, styles.kpi)}>
                        <ComponentLoader isLoading={isFetchingTotalTransactions}>
                            <ComponentValueChecker value={totalTransactions?.totalTransactions} isError={isErrorTotalTransactions}>
                                <div className={styles.kpiNumber}>{totalTransactions?.totalTransactions}</div>
                            </ComponentValueChecker>
                        </ComponentLoader>
                        <div className={styles.kpiLabel}>Number of chat sessions</div>
                    </div>
                    <div className={classNames(styles.col, styles.kpi)}>
                        <ComponentLoader isLoading={isFetchingAverageQuestions}>
                            <ComponentValueChecker value={averageQuestions?.averageQuestions} isError={isErrorAverageQuestions}>
                                <div className={styles.kpiNumber}>{averageQuestions?.averageQuestions}</div>
                            </ComponentValueChecker>
                        </ComponentLoader>
                        <div className={styles.kpiLabel}>Average questions per session</div>
                    </div>
                    <div className={classNames(styles.col, styles.kpi)}>
                        <ComponentLoader isLoading={isFetchingAverageDocs}>
                            <ComponentValueChecker value={averageDocs?.averageDocs} isError={isErrorAverageDocs}>
                                <div className={styles.kpiNumber}>{averageDocs?.averageDocs}</div>
                            </ComponentValueChecker>
                        </ComponentLoader>
                        <div className={styles.kpiLabel}>Average documents</div>
                    </div>
                    <div className={classNames(styles.col, styles.kpi)}>
                        <ComponentLoader isLoading={isFetchingAskedQuestions}>
                            <ComponentValueChecker value={askedQuestions?.questionsCount} isError={isErrorAskedQuestions}>
                                <div className={styles.kpiNumber}>{askedQuestions?.questionsCount}</div>
                            </ComponentValueChecker>
                        </ComponentLoader>
                        <div className={styles.kpiLabel}>Number of questions asked</div>
                    </div>
                </div>}
                {showQa && props.afterKpis}
                {(showQa || showUsers) && <div className={styles.rowContainer}>
                    {showUsers && <div className={classNames(styles.col, styles.card)}>
                        <div className={`${styles.sectionTitle} ${styles.mb2}`}>Users</div>
                        <ComponentLoader isLoading={isFetchingOrganisationUsers}>
                            <div className={styles.h46}>
                                <ComponentValueChecker value={organisationUsersMapped} isError={isErrorOrganisationUsers}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={organisationUsersMapped!}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={35}
                                                outerRadius={55}
                                                paddingAngle={3}
                                                label={renderNameValuePieLabel}
                                                labelLine={renderPieLabelLine}
                                            >
                                                {organisationUsersMapped!.map((e, i) => (
                                                    <Cell key={i} fill={e.colour} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </ComponentValueChecker>
                            </div>
                        </ComponentLoader>
                    </div>}
                    {showQa && <div className={classNames(styles.col, styles.card)}>
                        <div className={`${styles.sectionTitle} ${styles.mb2}`}>Knowledgebase usage</div>
                        <div className={styles.h46}>
                            <ComponentLoader isLoading={isFetchingInsightUsage}>
                                <ComponentValueChecker value={insightUsageMapped} isError={isErrorInsightUsage}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={insightUsageMapped}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={35}
                                                outerRadius={55}
                                                paddingAngle={3}
                                                label={renderNameValuePieLabel}
                                                labelLine={renderPieLabelLine}
                                            >
                                                {insightUsageMapped.map((e, i) => (
                                                    <Cell key={i} fill={e.colour} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </ComponentValueChecker>
                            </ComponentLoader>
                        </div>
                    </div>}
                </div>}

                {showUsers && <div className={styles.rowContainer}>
                    <div className={classNames(styles.col, styles.card)}>
                        <div className={`${styles.sectionTitle} ${styles.mb2}`}>User behaviours</div>
                        <div className={styles.h46}>
                            <ComponentLoader isLoading={isFetchingUserBehaviours}>
                                <ComponentValueChecker value={userBehavioursMapped} isError={isErrorUserBehaviours}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={userBehavioursMapped}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={35}
                                                outerRadius={55}
                                                paddingAngle={3}
                                                label={renderNameValuePieLabel}
                                                labelLine={renderPieLabelLine}
                                            >
                                                {userBehavioursMapped.map((e, i) => (
                                                    <Cell key={i} fill={e.colour} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </ComponentValueChecker>
                            </ComponentLoader>
                        </div>
                    </div>
                    <div className={classNames(styles.col, styles.card, callbackRequestsRequest && styles.splitCard)}>
                        <div className={styles.splitSection}>
                            <ComponentLoader isLoading={isFetchingFeedbackRatings}>
                                <div className={styles.kpiRatingSection}>
                                    <ComponentValueChecker value={feedbackRatingsMapped} isError={isErrorFeedbackRatings}>
                                        <div className={styles.kpiRatingGrid}>
                                            {feedbackRatingsMapped.map((item, index) => (
                                                <div key={`feedback_${index}`} className={styles.kpiRatingContainer}>
                                                    <div className={styles.kpiRatingIcon}>{item.icon}</div>
                                                    <div className={styles.kpiRatingNumber}>{item.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </ComponentValueChecker>
                                </div>
                            </ComponentLoader>
                            <div className={styles.kpiSectionLabel}>Feedback ratings</div>
                        </div>
                        {callbackRequestsRequest && (
                            <>
                                <div className={styles.sectionDivider} />
                                <div className={styles.splitSection}>
                                    <ComponentLoader isLoading={isFetchingCallbackRequests}>
                                        <div className={styles.kpiSingleValueSection}>
                                            <ComponentValueChecker value={callbackRequests?.total} isError={isErrorCallbackRequests}>
                                                <div className={styles.kpiNumber}>{callbackRequests?.total}</div>
                                            </ComponentValueChecker>
                                        </div>
                                    </ComponentLoader>
                                    <div className={styles.kpiSectionLabel}>Callback Requests</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>}
                {showUsers && <div className={styles.rowContainer}>
                    <div className={classNames(styles.col, styles.card, styles.h80)}>
                        <div className={`${styles.sectionTitle} ${styles.mb2}`}>Total visitors chats</div>
                        <div className={styles.h64}>
                            <ComponentLoader isLoading={isFetchingBehaviouralUsage} lines={5}>
                                <ComponentValueChecker value={behaviouralUsageMapped} isError={isErrorBehaviouralUsage}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={behaviouralUsageMapped} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="name"
                                                stroke={theme?.text_color}
                                                tick={{ fill: theme?.text_color }}
                                            />
                                            <YAxis
                                                stroke={theme?.text_color}
                                                tick={{ fill: theme?.text_color }}
                                            />
                                            <Tooltip labelStyle={{ color: theme?.text_color }} />
                                            <Line type="monotone" dataKey="value" strokeWidth={2} dot={false} stroke={theme?.primary_color} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </ComponentValueChecker>
                            </ComponentLoader>
                        </div>
                    </div>
                </div>}
                </>)}
            </div>
        </div >
    );
}


export default Dashboard;
